import { useEffect } from "react";
import { useItemLock } from "./useItemLock";
import type { Firestore } from "firebase/firestore";

type Params = {
  db: Firestore;
  lockMs: number;
  currentUserString: () => string | null;
  setEditIsReadOnly: (v: boolean) => void;
  setEditForm: (updater: (prev: any) => any) => void;
  isEditOpen: boolean;
  editIdRef: { current: string | null };
  isCustomerPortal: boolean;
  stopLockHeartbeatRef: { current: () => void };
  stopLockWatcherRef: { current: () => void };
};

export function useEditLocks({
  db,
  lockMs,
  currentUserString,
  setEditIsReadOnly,
  setEditForm,
  isEditOpen,
  editIdRef,
  isCustomerPortal,
  stopLockHeartbeatRef,
  stopLockWatcherRef,
}: Params) {
  const {
    startLockHeartbeat,
    stopLockHeartbeat,
    startLockWatcher,
    stopLockWatcher,
  } = useItemLock({
    db,
    lockMs,
    currentUserString,
    setEditIsReadOnly,
    onDocUpdate: (id, cur) => {
      if (editIdRef.current && editIdRef.current !== id) return;
      if (!isEditOpen) return;
      setEditForm((prev) => ({
        ...prev,
        deletePending: !!cur.deletePending,
        deleteMarkedBy: cur.deleteMarkedBy ?? null,
        deleteMarkedAt: cur.deleteMarkedAt ?? null,
      }));
      const me = currentUserString();
      const deleteOwnedByOther = !!cur.deletePending && !!cur.deleteMarkedBy && cur.deleteMarkedBy !== me;
      const shouldBeReadOnly = isCustomerPortal || !!cur.invoiceReportId || deleteOwnedByOther;
      setEditIsReadOnly(shouldBeReadOnly);
    },
  });

  useEffect(() => {
    stopLockHeartbeatRef.current = stopLockHeartbeat;
    stopLockWatcherRef.current = stopLockWatcher;
  }, [stopLockHeartbeat, stopLockHeartbeatRef, stopLockWatcher, stopLockWatcherRef]);

  return { startLockHeartbeat, startLockWatcher };
}
